// Content for SB 1.1.1 first explanation, part 1

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySloka: {
        reference: 'Srimad Bhagavatam 1.1.1',
        sloka: `oṁ namo bhagavate vāsudevāya
janmādy asya yato 'nvayād itarataś cārtheṣv abhijñaḥ svarāṭ
tene brahma hṛdā ya ādi-kavaye muhyanti yat sūrayaḥ |
tejo-vāri-mṛdāṁ yathā vinimayo yatra tri-sargo 'mṛṣā
dhāmnā svena sadā nirasta-kuhakaṁ satyaṁ paraṁ dhīmahi ||`,
        translation: `Let us meditate upon the Absolute Truth, Kṛṣṇa (<strong>satyaṁ paraṁ dhīmahi</strong>), who is the cause of creation, maintenance and destruction of this universe (<strong>janmādy asya yataḥ</strong>), as the material and efficient cause (<strong>anvayād itarataś ca</strong>), since he is the only knower of all objects (<strong>artheṣv abhijñaḥ</strong>) and the only independent being (<strong>svarāṭ</strong>); who revealed the Vedas to Brahmā within his mind (<strong>tene brahma hṛdā ya ādi-kavaye</strong>); who is difficult to understand even for the learned (<strong>muhyanti yat sūrayaḥ</strong>); whose body is thought to be made of matter due to illusion just as one mistakes fire, water and earth for each other (<strong>tejo-vāri-mṛdāṁ yathā vinimayo yatra tri-sargo ’mṛṣā</strong>); and who negates all false arguments regarding his existence by possessing a transcendental, inconceivable form, and by the strength of devotees’ experiencing him though his svarūpa-śakti (<strong>dhāmnā svena sadā nirasta-kuhakaṁ</strong>).`,
    },
    blocks: [
        {
            type: 'heading',
            headingText: 'First Explanation',
            content:
                'In the beginning of the work, the author, Śrī-kṛṣna-dvaipāyana - the crown jewel among ācāryas - invokes auspiciousness with meditation on his cherished deity.',
        },
        {
            type: 'text',
            content:
                '<strong><em>Param</em> means “to the highest limit.” <em>Satyam</em> means “that Supreme Lord who exists in all time and space.” <em>Dhīmahi</em> means “let us worship or meditate on.”</strong> The plural indicates all the jīvas continuing in time and space as part of one’s own group and thus indicates teaching them meditation by these instructions. The meaning of the sūtra, athāto brahma jijñāsā (Vedānta-sūtra 1.1.1) is indicated from this since meditation alone is the result of inquiry.',
        },
        {
            type: 'heading',
            headingText: 'Janmādy Asya Yataḥ and the Lord as Cause',
            content:
                'The Lord’s supreme power is indicated in this verse with janmādy asya yataḥ. (Vedānta-sūtra 1.1.2) (Let us meditate on the Supreme Lord) from whom (yataḥ) arises creation, maintenance and destruction (janmādi) of the universe (asya). Should they meditate on time, which causes all this?',
        },
        {
            type: 'dialogue',
            objection: 'Should they meditate on time, which causes all this?',
            response:
                'No. The Lord is the cause because he is the material and efficient cause (anvyād itarataś ca). Anvayād itarataḥ (anvaya and its opposite) can mean anvaya-vyatireka, which, in talking about causality, can refer to cause and effect. The Lord in relation to the universe is like the earth which, as a material cause, is inherent in the pot, and the pot which, as an effect, is inherent in its material cause, the earth.<br /><br />Thus this phrase means that the Lord is the material cause (upādāna-kāraṇa). The word ca indicates the efficient cause (nimitta-kāraṇa) which is time, because the Lord takes the form of time to influence prakṛti.<br /><br /><strong>Thus the Lord is the cause (janma) by being the material (anvayād itarataḥ) and the efficient cause (ca).</strong>',
        },
        {
            type: 'text',
            content:
                'Or the word anvaya (meaning inclusion or entrance into) can also indicate that the Lord is the cause and destruction (janmādi) because everything enters the Lord. The universes enter into the Lord at destruction (and issues from him at the time of creation). Itarataḥ then indicates divisions of matter taking place at the level of secondary creation outside the Lord. That means that the Lord is the basis (adhisthātṛ-kāraṇa) of the whole universe, just as water is the basis of earth, and fire is the basis of water.<br /><br /><strong>Thus the Lord is that person from whom creation, maintenance and destruction takes place because everything is contained within him (anvayāt) and everything in the secondary creation is outside him, but based on him (itarataḥ).</strong>',
        },
        {
            type: 'text',
            content:
                'Or the word anvaya (meaning sequence) can mean that the Lord is creation and destruction (janmādi), because he is the whole sequence of creation, maintenance and destruction. The Lord enters into the universe, as the final agent of causality, in the process of creation; he enters the universe as the final agent for dispensing results of action in maintenance; and he enters the universe in the form of Śiva as the final agent in the process of destruction.<br /><br />In this explanation, it should be understood that the cause includes within itself the effect, and the Lord as cause enters into the effect, the universe. Thus the Lord is identified as creation, maintenance and destruction of the universe. The universe itself is kept at a distance from the svarūpa of the Lord by the use of the descriptive word itarataḥ (different), since the creation, maintenance and destruction of the universe is different from the Lord’s svarūpa-śakti. Ca indicates it is non-different from the māyā-śakti.<br /><br /><strong>Thus anvayād itarataḥ ca means the Lord is the creator, maintainer and destroyer since he is non-different from the universe in its phases of creation, maintenance and destruction, but this universe is different from his svarūpa, and non-different from his māyā-śakti.</strong>',
        },
        {
            type: 'text',
            content:
                'Thus in the first line the two Vedānta-sūtras, janmādy asya (1.1.2) and tat tu samanvayāt (1.1.3), have been spoken.',
        },
        {
            type: 'dialogue',
            objection:
                '“But if the Supreme Lord is said to be the material cause of the universe, he should be devoid of change. Therefore should one not say that the Supreme Lord is the efficient and prakṛti is the material cause.”',
            response:
                'No. It is not so. The śrutis say yaḥ sarvajñāh sarva-vit: he, who is omniscient, knows all. (Muṇḍaka Upaniṣad 2.2.7) Sa īkṣata lokān: he glanced over the worlds and then created (Aitareya Upaniṣad); and tad aikṣata bahusyāṁ prajāyeya; he glanced and said, “Let me be many, Let me create progeny.” (Chāndogya Upaniṣad 6.2.3) These verses indicate that only a conscious entity is the cause of the universe, and thus the Lord is both the material cause and the efficient cause of the universe.<br /><br />Since prakṛti is a śakti of the Lord and the śakti and possessor of the śakti are non-different, the Lord is the material cause through prakṛti. <strong>But the Lord remains unchanged in spite of being the material cause, because by his very nature he transcends prakṛti.</strong>',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 11.24.19',
                sloka: `prakṛtir yasyopādānam
ādhāraḥ puruṣaḥ paraḥ |
sato 'bhivyañjakaḥ kālo
brahma tat tritayaṁ tv aham ||`,
                translation:
                    'Prakrṭi is the material cause, the puruṣa is the foundational cause. Time, the indirect cause, is agitator of prakṛti. I am all three.',
            },
        },
        {
            type: 'text',
            content:
                'Scripture does not state that prakṛti is the material cause independently. The Lord, conscious of all things, is alone the cause of the universe by his independence. Unconscious prakṛti is not the cause. Thus the verse says that the Lord is fully conscious (abhijñaḥ) concerning all matters relating to the creation and destruction of all real objects (artheṣu). This statement illustrates the meaning of īkṣater nāśabdam: being described in the scriptures, the Lord is not beyond the description of words (though he remains beyond the material). (Vedānta-sūtra 1.1.5)',
        },
        {
            type: 'text',
            content:
                'The meaning of the sūtra is this. The brahman which was discussed is the cause of the universe. Why? Because of seeing; because of specialized conclusions arising from seeing, or in other words, from hearing about the Lord in the statements of śruti which describe him as the cause of the universe. <strong>Therefore brahman is not indescribable. It is not that the Lord cannot be proved by authoritative words. He can be proved by the scriptures.</strong>',
        },
        {
            type: 'heading',
            headingText: 'Śruti and Smṛti Citations for the Conscious Cause',
            content: 'The śrutis state that the conscious Lord is the cause:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Chāndogya Upaniṣad 6.2.3',
                sloka: 'tad aikṣata bahu syām prajāyeya',
                translation: 'He glanced at prakṛti. May I become many; let me create progeny.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Chāndogya Upaniṣad 6.2.1',
                sloka: 'sad eva saumyedam agra asīt',
                translation: 'O gentle one! The eternal Lord existed before this universe.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Chāndogya Upaniṣad 1.1.1',
                sloka: 'ātmā vā idam eka evāgra asīt',
                translation: 'The Lord existed before this universe.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Taittirīya Upaniṣad 1',
                sloka: 'tasmād vā etasmād ātmana ākāśaḥ sambhūta',
                translation: 'From that Lord arose the ether.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Taittirīya Brāhmaṇa 1',
                sloka: 'yato vā imāni bhūtāni jāyante',
                translation: 'From the Lord all creatures arise.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Smṛti',
                sloka: `yataḥ sarvāṇi bhūtāni
bhavanty ādi-yugāgame |
yasmiṁś ca pralayaṁ yānti
punar eva yuga-kṣaye ||`,
                translation:
                    'From the Lord all creatures arise at the beginning of the first yuga and in him they merge at the time of universal destruction.',
            },
        },
        {
            type: 'text',
            content:
                'One may object that the mahat-tattva and other elements had not arisen so that he could have a body which could perform actions. Therefore the verse says that the Lord is independent (svarāṭ). He controls everything by himself (svayam rājate) through his spiritual svarūpa (non-different from himself). Thus the śruti says na tasya kāryam kāraṇaṁ ca vidyate… svābhāvikī-jñāna-bala-kriya ca: In the Lord there is no material cause and effect; he has his own inherent knowledge, strength and action. (Śvetāśvatara Upaniṣad 6.8)',
        },
        {
            type: 'heading',
            headingText: 'Brahmā is Dependent, Not Independent',
            content:
                'One may object that in the creation of the universe, one should understand that Lord Brahmā has independent powers, for in the śruti it is said hiraṇyagarbhaḥ samavartatāgre bhūtasya jātaḥ patir eka asīt: Brahmā was born before other creatures; he alone existed. (Mahā-nārāyaṇa Upaniṣad 6) Therefore Brahmā should be the object of worship.',
        },
        {
            type: 'text',
            content:
                'The verse answers this objection in the second line. It is the Lord, satyam param, who revealed (tene) the Vedas (brahma) — knowledge of himself — to Brahmā (ādi-kavaye). Thus Brahmā is dependent on the Lord. One may object that it is well known that Brahmā did not study the Vedas from anyone. That is true. He received it in his mind (hṛdaye). This is stated in the Bhāgavatam.',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 2.4.22',
                sloka: `pracoditā yena purā sarasvatī
vitanvatājasya satīṁ smṛtiṁ hṛdi
sva-lakṣaṇā prādurabhūt kilāsyataḥ
sa me ṛṣīṇām ṛṣabhaḥ prasīdatām`,
                translation:
                    'May the Lord, the best of the sages, be pleased with me! Inspired by him, at the beginning of the kalpa, Sarasvatī, whose aim is to reveal Kṛṣṇa, appeared from the mouth of Brahmā and revealed proper memory to carry out creation in his heart.',
            },
        },
        {
            type: 'text',
            content:
                'As well sudṛṣṭaṁ hṛḍi me tadaiva: why did I not see him in my heart at that time? (SB 10.14.15) The meaning of the gāyatrī mantra was revealed to him by that method. It is said in the Matsya Purāṇa (53.20):',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Matsya Purāṇa 53.20',
                sloka: `yatrādhikṛtya gāyatrīṁ
varṇyate dharma-vistaraḥ |
vṛtrāsura-vadhopetaṁ
tad bhāgavatam iṣyate ||`,
                translation:
                    'He spoke the Bhāgavatam where the killing of Vṛtrāsura is described and where, after starting with gāyatrī, dharma is elaborately described.',
            },
        },
        {
            type: 'text',
            content: 'In another Purāṇa it is said:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Another Purāṇa',
                sloka: `grantho 'ṣṭādaśa-sāhasro
dvādaśa-skandha-saṁmitaḥ |
hayagrīva-brahma-vidyā
yatra vṛtra-vadhas tathā |
gāyatryā ca samārambhas
tad vai bhāgavataṁ viduḥ ||`,
                translation:
                    'The Bhāgavatam is understood to be that work starting with gāyatrī mantra in which there are eighteen thousand verses and twelve volumes, and in which spiritual knowledge spoken by Hayagrīva and the killing of Vṛtra are described.',
            },
        },
        {
            type: 'dialogue',
            objection:
                'Someone may argue: “Perhaps Brahmā realized the truth of the Vedas on his own (from within the mind) just as a person sometimes gets a realization during sleep.”',
            response:
                'To answer this argument, then it is said that Brahmā, independently, does not have the power to realize this knowledge, for even the greatly learned are bewildered about this (yad sūrayaḥ muhyanti). This explains the following sūtra. Etena netaro ’nupapatteḥ: a jīva is not described (in the mantra "satyam jñānam anantam brahma"), because such an interpretation of the mantra is illogical. (Vedānta-sūtra 1.1.16)',
        },
        {
            type: 'heading',
            headingText: 'Objection About Form and the Three Guṇas',
            content:
                'There is another objection. When we talk about meditation it indicates that we meditate on an object that has a form. Forms are made of the three guṇas of matter, and must be therefore temporary.',
        },
        {
            type: 'text',
            content:
                'This objection is answered in the third line. It is like reversal; or one thing appearing as another (vinimayaḥ), just as light may appear to be water, or water may appear to be earth or earth in forms like glass may appear to be like water to an ignorant person. In this way one falsely (mṛṣā) thinks that the perfect, spiritual form of the Lord to be made of the three guṇas (tri-sargaḥ).',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Gopāla-tāpanī Upaniṣad 1.33',
                sloka: 'tam ekaṁ govindaṁ sac-cid-ānanda-vigrahaṁ vṛndāvana-sura-bhūruha-talāsīnam',
                translation:
                    'I saw that one form of Govinda, a form of eternity, knowledge and bliss, seated at the base of desire tree in Vṛndāvana.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Rāma-tāpanī Upaniṣad',
                sloka: 'ardha-mātrātmako rāmo brahmānandaika-vigrahaḥ',
                translation: 'Rāma is the half-syllable and form of spiritual bliss.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Nṛsiṁha-tāpanī Upaniṣad',
                sloka: 'ṛtaṁ satyaṁ paraṁ brahma puruṣaṁ nṛ-keśari-vigraham',
                translation: 'The form of Nṛsiṁha is the supreme brahman, the puruṣa, knowledge and truth.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Dhyāna-bindu Upaniṣad',
                sloka: `nirdoṣa-pūrṇa-guṇa-vigraha ātma-tantro
niścetanātmaka-śarīra-guṇaiś ca hīnaḥ |
ānanda-mātra-kara-pāda-mukhodarādiḥ ca |`,
                translation:
                    'The Lord has a form full of faultless qualities, which is independent. He is devoid of the qualities of lifeless, material bodies. All the parts of his body such as hands, feet, head and belly are bliss alone.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Brahmāṇḍa Purāṇa 2.36.25',
                sloka: 'nanda-vraja-janānandī sac-cidānanda-vigrahaḥ',
                translation: 'Kṛṣṇa has a form of eternity, knowledge and bliss which gives joy to the people of Vraja.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Mahā varāha Purāṇa',
                sloka: `sarve nityāḥ śāśvatāś ca
dehās tasya parātmanaḥ |
hānopādāna-rahitā
naiva prakṛti-jāḥ kvacit ||`,
                translation:
                    'The bodies of the Lord are all eternal, unchanging, and devoid of faults. They are never the product of matter.',
            },
        },
        {
            type: 'text',
            content: 'This is also understood from the Bhāgavatam:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 10.14.2',
                sloka: `asyāpi deva vapuṣo mad-anugrahasya
svecchā-mayasya na tu bhūta-mayasya ko 'pi
neśe mahi tv avasituṁ manasāntareṇa
sākṣāt tavaiva kim utātma-sukhānubhūteḥ`,
                translation:
                    'My dear Lord, neither I nor anyone else can estimate the potency of this transcendental body of yours, which has shown such mercy to me and which appears just to fulfill the desires of your pure devotees. Although my mind is completely withdrawn from material affairs, I cannot understand your personal form. How, then, could I possibly understand the happiness you experience within yourself?',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 10.9.14',
                sloka: `taṁ matvātmajam avyaktaṁ
martya-liṅgam adhokṣajam
gopikolūkhale dāmnā
babandha prākṛtaṁ yathā`,
                translation:
                    'Being absolute, beyond relativity, he is free from distinctions between cause and effect, although he is the cause and effect of everything. That unmanifested person, who is beyond the perception of the senses, had now appeared as a human child, and mother Yaśodā, considering him her own ordinary child, bound Him to the wooden mortar with a rope.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 10.14.22',
                sloka: `tasmād idaṁ jagad aśeṣam asat-svarūpaṁ
svapnābham asta-dhiṣaṇaṁ puru-duḥkha-duḥkham
tvayy eva nitya-sukha-bodha-tanāv anante
māyāta udyad api yat sad ivāvabhāti`,
                translation:
                    'Therefore this entire universe, which like a dream is by nature unreal, nevertheless appears real, and thus it covers one’s consciousness and assails one with repeated miseries. This universe appears real because it is manifested by the potency of illusion emanating from you, whose unlimited transcendental forms are full of eternal happiness and knowledge.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 3.21.8',
                sloka: `tāvat prasanno bhagavān
puṣkarākṣaḥ kṛte yuge
darśayām āsa taṁ kṣattaḥ
śābdaṁ brahma dadhad vapuḥ`,
                translation:
                    'Then, in the Satya-yuga, the lotus-eyed Supreme Personality of Godhead, being pleased, showed himself to that Kardama Muni and displayed his transcendental form, which can be understood only through the Vedas.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 10.13.54',
                sloka: `satya-jñānānantānanda-
mātraika-rasa-mūrtayaḥ
aspṛṣṭa-bhūri-māhātmyā
api hy upaniṣad-dṛśām`,
                translation:
                    'The viṣṇu-mūrtis all had eternal, unlimited forms, full of knowledge and bliss and existing beyond the influence of time. Their great glory was not even to be touched by the jñānīs engaged in studying the Upaniṣads.',
            },
        },
        {
            type: 'text',
            content:
                'Even the devotees in Śvetadvīpa and Vaikuṇṭha have forms. These forms are not material, since the word atīndriya is used. Nārāyaṇīya says:',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Mahābhārata 12.323.26',
                sloka: `anindriyā anāhārā
aniṣpannāḥ sugandhinaḥ |
ekāntinas te puruṣāḥ
śveta-dvīpa-nivāsinaḥ ||`,
                translation:
                    'The inhabitants living in Śvetadvīpa, devoted completely to the Lord, are all fragrant, beyond the material senses, without any need for material food and without material movement.',
            },
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Srimad Bhagavatam 7.1.35',
                sloka: `dehendriyāsu-hīnānāṁ
vaikuṇṭha-pura-vāsinām
deha-sambandha-sambaddham
etad ākhyātum arhasi`,
                translation:
                    'The bodies of the inhabitants of Vaikuṇṭha are completely spiritual, having nothing to do with the material body, senses or life air. Therefore, kindly explain how associates of the Lord were cursed to descend in material bodies like ordinary persons.',
            },
        },
        {
            type: 'heading',
            headingText: 'Conclusion: Inconceivable Reality Is Not Subject to Material Logic',
            content:
                'What doubt can there be that their bodies are non-material? Some persons argue with all these conclusions. In answer to this, the following is said.<br /><br /><strong>Through the power of realization of the Lord fixed in the devotee’s heart by the svarūpa-śakti, or through the Lord’s form shining with power and sweetness increasing at every moment (dhāmnā), which belongs only to the Lord and which is thus extraordinary (svena), throughout all three phases of time (sadā), all false arguments (kuhakam) about the Lord are negated (nirasta).</strong> This is indicated in tarko ’pratiṣṭhānāt: argumentation is not accepted concerning the Lord, because it is insubstantial. (Vedānta-sutra 2.1.11)<br /><br />yam evaiṣa vṛṇute tena labhyas tasyaiṣa ātmā vivṛṇute tanuṁ svām: the Lord reveals his form to that person whom he chooses. (Muṇḍaka Upaniṣad 3.2.3) The use of the word svām to modify tanum indicates that the body of the Lord arises from his svarūpa-śakti. That his mind and eyes are not material is also understood from the statements bahu syām (let me become many) and sa aikṣata (he glanced), since these senses are employed before the agitation of prakṛti, which produces material mind and senses. <strong>As well parāsya śaktir vividhaiva śrūyate svābhāvikī jñāna-bala-kriyā ca (Śvetāśvatara Upaniṣad 6.8) indicates that his knowledge, strength and actions arise from his own nature (svābhāvikī), not prakṛti.</strong>',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Mahābhārata 6.6.11',
                sloka: `acintyāḥ khalu ye bhāvā
na tāṁs tarkeṇa yojayet |
prakṛtibhyaḥ paraṁ yac ca
tad acintyasya lakṣaṇam ||`,
                translation:
                    'One cannot use material reasoning on those things which are inconceivable. Inconceivable refers to those things existing beyond prakṛti.',
            },
        },
        {
            type: 'text',
            content:
                '<strong>The potential mood indicates that material arguments are forbidden to be used against the Lord. It is just as strong as the prohibition: para-dārān na gacchet: one should not have sex with other men’s wives. If the demons, who eagerly take up arrows of logic aimed at the Lord, fall to hell, let them fall there. Enough of their discussions!</strong>',
        },
    ],
};

export default content;
